import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import {
  adminListInvoiceEmailLogs,
  adminResendInvoiceEmail,
  adminResendPayfastReceipt,
  adminRunInvoiceReceiptSelfTest,
} from "@/lib/api/payments-admin.functions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Card, CardContent, CardHeader, CardTitle,
} from "@/components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Beaker, Download, Eye, Mail, RefreshCcw, Loader2,
  CheckCircle2, XCircle, AlertTriangle,
} from "lucide-react";

export const Route = createFileRoute(
  "/_authenticated/portal/admin/invoice-emails",
)({
  component: InvoiceEmailsAdmin,
});

const STATUSES = ["all", "pending", "sent", "failed", "dlq", "bounced", "complained", "suppressed"] as const;
const TEMPLATES = ["all", "invoice-created", "payfast-receipt", "payment-status-update"] as const;

function StatusBadge({ status }: { status: string }) {
  const s = (status || "").toLowerCase();
  if (s === "sent") return <Badge className="bg-emerald-600 text-white">sent</Badge>;
  if (s === "pending") return <Badge className="bg-amber-500 text-white">queued</Badge>;
  if (s === "failed" || s === "dlq") return <Badge className="bg-red-600 text-white">{s}</Badge>;
  if (s === "bounced" || s === "complained" || s === "suppressed")
    return <Badge className="bg-red-700 text-white">{s}</Badge>;
  return <Badge variant="outline">{s || "—"}</Badge>;
}

function fmtDate(d?: string | null) {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleString("en-ZA", { dateStyle: "short", timeStyle: "short" });
  } catch {
    return d;
  }
}

function InvoiceEmailsAdmin() {
  const list = useServerFn(adminListInvoiceEmailLogs);
  const resendInvoice = useServerFn(adminResendInvoiceEmail);
  const resendReceipt = useServerFn(adminResendPayfastReceipt);
  const runSelfTest = useServerFn(adminRunInvoiceReceiptSelfTest);

  const [reference, setReference] = useState("");
  const [templateName, setTemplateName] =
    useState<(typeof TEMPLATES)[number]>("all");
  const [status, setStatus] = useState<(typeof STATUSES)[number]>("all");
  const [page, setPage] = useState(1);
  const pageSize = 50;
  const [busy, setBusy] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<any>(null);
  const [testRef, setTestRef] = useState("");

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["admin-invoice-emails", reference, templateName, status, page],
    queryFn: () =>
      list({ data: { reference: reference || undefined, templateName, status, page, pageSize } }),
    refetchInterval: 15_000,
  });

  const rows: any[] = (data as any)?.rows ?? [];
  const total: number = (data as any)?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / pageSize));

  async function handleResend(row: any) {
    if (!row.reference) {
      toast.error("This row has no order reference to resend.");
      return;
    }
    setBusy(row.id);
    try {
      const isReceipt = row.template_name === "payfast-receipt";
      const r: any = isReceipt
        ? await resendReceipt({ data: { reference: row.reference } })
        : await resendInvoice({ data: { reference: row.reference } });
      if (r?.ok) toast.success(`${isReceipt ? "Receipt" : "Invoice"} resent`);
      else if (r?.queued) toast.info("Queued for retry");
      else toast.error(r?.message || r?.reason || "Failed");
    } catch (e: any) {
      toast.error(e?.message || "Failed");
    } finally {
      setBusy(null);
      refetch();
    }
  }

  async function handleSelfTest() {
    setBusy("selftest");
    setTestResult(null);
    try {
      const r: any = await runSelfTest({
        data: { reference: testRef.trim() || undefined, sendEmail: true },
      });
      setTestResult(r);
      if (r?.ok) toast.success("Self-test passed");
      else toast.error(r?.message || r?.reason || "Self-test failed");
    } catch (e: any) {
      toast.error(e?.message || "Failed");
    } finally {
      setBusy(null);
      refetch();
    }
  }

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold">Invoice & receipt emails</h1>
        <p className="text-sm text-muted-foreground">
          End-to-end delivery log for proforma invoice and PayFast receipt
          emails — including retry counts, failure reasons, and one-click
          resend.
        </p>
      </header>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Beaker className="h-4 w-4" /> One-click verification
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Generates a sample invoice PDF and receipt PDF from the latest
            paid PayFast order (or a specific reference), confirms both
            download as valid PDFs, and emails you a confirmation with both
            links.
          </p>
          <div className="flex flex-wrap items-end gap-2">
            <div className="grow min-w-[220px]">
              <Label htmlFor="selftest-ref">Reference (optional)</Label>
              <Input
                id="selftest-ref"
                placeholder="Leave blank to use latest paid order"
                value={testRef}
                onChange={(e) => setTestRef(e.target.value)}
              />
            </div>
            <Button onClick={handleSelfTest} disabled={busy === "selftest"}>
              {busy === "selftest" ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Beaker className="mr-2 h-4 w-4" />
              )}
              Run verification
            </Button>
          </div>
          {testResult ? (
            <div className="rounded border bg-muted/30 p-3 text-sm">
              <div className="flex items-center gap-2 font-medium">
                {testResult.ok ? (
                  <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                ) : (
                  <XCircle className="h-4 w-4 text-red-600" />
                )}
                {testResult.ok ? "All checks passed" : "Test failed"} — ref{" "}
                <code className="font-mono text-xs">{testResult.reference}</code>
              </div>
              <ul className="mt-2 space-y-1 text-xs">
                {testResult.checks?.invoicePdf ? (
                  <li>
                    Invoice PDF:{" "}
                    {testResult.checks.invoicePdf.ok ? (
                      <span className="text-emerald-700">
                        ok ({testResult.checks.invoicePdf.bytes} bytes)
                      </span>
                    ) : (
                      <span className="text-red-700">
                        {testResult.checks.invoicePdf.reason}
                      </span>
                    )}{" "}
                    —{" "}
                    <a
                      className="underline"
                      href={testResult.sampleUrls?.invoiceUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      open
                    </a>
                  </li>
                ) : null}
                {testResult.checks?.receiptPdf ? (
                  <li>
                    Receipt PDF:{" "}
                    {testResult.checks.receiptPdf.ok ? (
                      <span className="text-emerald-700">
                        ok ({testResult.checks.receiptPdf.bytes} bytes)
                      </span>
                    ) : (
                      <span className="text-red-700">
                        {testResult.checks.receiptPdf.reason}
                      </span>
                    )}{" "}
                    —{" "}
                    <a
                      className="underline"
                      href={testResult.sampleUrls?.receiptUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      open
                    </a>
                  </li>
                ) : null}
                {testResult.checks?.emailSent ? (
                  <li>
                    Email:{" "}
                    {testResult.checks.emailSent.ok ? (
                      <span className="text-emerald-700">queued for delivery</span>
                    ) : (
                      <span className="text-red-700">
                        {testResult.checks.emailSent.reason}
                      </span>
                    )}
                  </li>
                ) : null}
              </ul>
            </div>
          ) : null}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
          <CardTitle className="text-base">Delivery log</CardTitle>
          <div className="flex flex-wrap items-end gap-2">
            <div>
              <Label htmlFor="ref">Reference</Label>
              <Input
                id="ref"
                placeholder="search ref…"
                value={reference}
                onChange={(e) => { setReference(e.target.value); setPage(1); }}
                className="w-40"
              />
            </div>
            <div>
              <Label>Template</Label>
              <Select value={templateName} onValueChange={(v: any) => { setTemplateName(v); setPage(1); }}>
                <SelectTrigger className="w-48"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TEMPLATES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Status</Label>
              <Select value={status} onValueChange={(v: any) => { setStatus(v); setPage(1); }}>
                <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Button variant="outline" size="sm" onClick={() => refetch()}>
              <RefreshCcw className="mr-2 h-4 w-4" /> Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center gap-2 py-8 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </div>
          ) : rows.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">
              No delivery records match these filters.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>When</TableHead>
                    <TableHead>Template / doc</TableHead>
                    <TableHead>Reference</TableHead>
                    <TableHead>Recipient</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Retries</TableHead>
                    <TableHead>Error</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((r) => {
                    const isReceipt = r.template_name === "payfast-receipt";
                    // Direct PDF links require the order's unguessable
                    // public_token, which we don't fetch on this admin log
                    // view. Route admins to the order page where the
                    // download button is scoped by the token instead.
                    const pdfUrl = r.reference
                      ? `/payment-status/${encodeURIComponent(r.reference)}`
                      : null;


                    return (
                      <TableRow key={r.id}>
                        <TableCell className="text-xs text-muted-foreground">{fmtDate(r.created_at)}</TableCell>
                        <TableCell className="text-xs">
                          <div className="font-medium">{r.template_name}</div>
                          {r.doc ? (
                            <div className="text-[10px] text-muted-foreground uppercase">{r.doc}</div>
                          ) : null}
                        </TableCell>
                        <TableCell className="font-mono text-xs">{r.reference ?? "—"}</TableCell>
                        <TableCell className="text-xs">{r.recipient_email}</TableCell>
                        <TableCell><StatusBadge status={r.status} /></TableCell>
                        <TableCell className="text-xs">
                          {r.retry?.attempts ? (
                            <span className="inline-flex items-center gap-1">
                              <AlertTriangle className="h-3 w-3 text-amber-600" />
                              {r.retry.attempts}
                            </span>
                          ) : "0"}
                        </TableCell>
                        <TableCell className="max-w-[260px] text-xs text-red-700">
                          {r.error_message || r.retry?.last_error || ""}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex flex-wrap justify-end gap-1">
                            {pdfUrl ? (
                              <>
                                <Button asChild size="sm" variant="outline">
                                  <a href={pdfUrl} target="_blank" rel="noopener noreferrer">
                                    <Eye className="h-3 w-3" />
                                  </a>
                                </Button>
                                <Button asChild size="sm" variant="outline">
                                  <a href={pdfUrl} download>
                                    <Download className="h-3 w-3" />
                                  </a>
                                </Button>
                              </>
                            ) : null}
                            {r.reference ? (
                              <Button
                                size="sm"
                                variant="secondary"
                                disabled={busy === r.id}
                                onClick={() => handleResend(r)}
                              >
                                {busy === r.id ? (
                                  <Loader2 className="h-3 w-3 animate-spin" />
                                ) : (
                                  <Mail className="h-3 w-3" />
                                )}
                              </Button>
                            ) : null}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>

              <div className="mt-3 flex items-center justify-between text-xs text-muted-foreground">
                <div>{total} record{total === 1 ? "" : "s"}</div>
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Prev</Button>
                  <span>page {page} / {pages}</span>
                  <Button size="sm" variant="outline" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>Next</Button>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
